import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #inputListeners = null;

  connectedCallback() {
    super.connectedCallback();
    this.#inputListeners = new AbortController();
    const options = { signal: this.#inputListeners.signal };
    const trigger = this.refs.details.querySelector(':scope > summary');
    trigger.addEventListener('click', this.#onTriggerClick, options);
    this.refs.menuDrawer.addEventListener('keydown', this.#onKeyboard, options);
    this.refs.menuDrawer.addEventListener('pointerdown', this.#onPointer, options);
    this.refs.details.addEventListener('toggle', this.#onToggle);
    this.refs.menuDrawer.addEventListener('cancel', this.#onCancel);
    this.refs.menuDrawer.addEventListener('close', this.#onClose);
    this.refs.menuDrawer.addEventListener('click', this.#onBackdrop);
    window.addEventListener('pageshow', this.#onPageShow);
    if (this.refs.details.open && !this.isOpen) this.open();
  }

  disconnectedCallback() {
    this.#inputListeners?.abort();
    this.close();
    this.refs.details.removeEventListener('toggle', this.#onToggle);
    this.refs.menuDrawer.removeEventListener('cancel', this.#onCancel);
    this.refs.menuDrawer.removeEventListener('close', this.#onClose);
    this.refs.menuDrawer.removeEventListener('click', this.#onBackdrop);
    window.removeEventListener('pageshow', this.#onPageShow);
    super.disconnectedCallback();
  }

  #onTriggerClick = (event) => {
    event.preventDefault();
    this.toggle();
  };
  #onKeyboard = (event) => {
    if (event.key === 'Tab') this.refs.menuDrawer.setAttribute('data-keyboard-navigation', '');
  };
  #onPointer = () => { this.refs.menuDrawer.removeAttribute('data-keyboard-navigation'); };

  get isOpen() { return this.refs.menuDrawer.open; }

  #onToggle = () => {
    if (this.refs.details.open && !this.isOpen) this.open();
    else if (!this.refs.details.open && this.isOpen) this.close();
  };
  #onCancel = (event) => { event.preventDefault(); this.close(); };
  #onClose = () => { if (!this.isOpen) this.#reset(); };
  #onPageShow = () => { if (!this.isOpen) this.#reset(); };
  #onBackdrop = (event) => {
    if (event.target !== this.refs.menuDrawer) return;
    const rect = this.refs.menuDrawer.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
  };

  toggle(event) {
    event?.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  open(target, event) {
    event?.preventDefault();
    const { details, menuDrawer } = this.refs;
    const submenu = event?.target instanceof Element ? event.target.closest('details') : null;
    if (target && submenu && submenu !== details) {
      submenu.open = true;
      submenu.classList.add('menu-open');
      submenu.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      submenu.querySelector('.menu-drawer__back-button')?.focus({ preventScroll: true });
      return;
    }
    if (this.isOpen) return;
    details.open = true;
    details.classList.add('menu-open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    try {
      menuDrawer.showModal();
      lockScroll(menuDrawer);
    } catch (error) {
      this.close();
      throw error;
    }
  }

  back(event) {
    event?.preventDefault();
    const submenu = event?.target instanceof Element ? event.target.closest('details') : null;
    if (!submenu || submenu === this.refs.details) return this.close();
    submenu.classList.remove('menu-open');
    submenu.open = false;
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    const summary = submenu.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus({ preventScroll: true });
  }

  close() {
    const wasOpen = this.isOpen;
    if (wasOpen) this.refs.menuDrawer.close();
    this.#reset();
    if (wasOpen && this.isConnected) this.refs.details.querySelector('summary')?.focus({ preventScroll: true });
  }

  #reset() {
    const { details, menuDrawer } = this.refs;
    unlockScroll(menuDrawer);
    unlockScroll(details);
    details.classList.remove('menu-open');
    details.open = false;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    menuDrawer.querySelectorAll('details.menu-open').forEach(submenu => {
      submenu.classList.remove('menu-open');
      submenu.open = false;
      submenu.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }
}

if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
